import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { Globe2 as OrbitIcon, Users, Loader2, CheckCircle, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../utils/api';
import { toast } from 'sonner';

const JoinOrbit = () => {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [orbitPreview, setOrbitPreview] = useState(null);
  const [error, setError] = useState(null);
  const [joined, setJoined] = useState(false);

  const t = {
    en: {
      title: 'Join Orbit',
      youreInvited: "You're invited to join",
      members: 'members',
      status: 'Status',
      mission: 'Mission',
      joinNow: 'Join Orbit',
      joining: 'Joining...',
      invalidLink: 'Invalid or expired invite link',
      loginRequired: 'Please sign in to join this Orbit',
      signIn: 'Sign In',
      joined: 'Successfully Joined!',
      goToOrbit: 'Go to Orbit',
    },
    tr: {
      title: 'Orbit\'e Katıl',
      youreInvited: "Katılmaya davetlisiniz",
      members: 'üye',
      status: 'Durum',
      mission: 'Misyon',
      joinNow: 'Orbit\'e Katıl',
      joining: 'Katılınıyor...',
      invalidLink: 'Geçersiz veya süresi dolmuş davet linki',
      loginRequired: 'Bu Orbit\'e katılmak için giriş yapın',
      signIn: 'Giriş Yap',
      joined: 'Başarıyla Katıldınız!',
      goToOrbit: 'Orbit\'e Git',
    }
  };

  const c = t[language] || t.en;

  useEffect(() => {
    fetchOrbitPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteCode]);

  const fetchOrbitPreview = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/orbit/join/${inviteCode}`);
      setOrbitPreview(response.data);
    } catch (error) {
      console.error('Error fetching orbit preview:', error);
      setError(c.invalidLink);
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    // Check if user is logged in
    const token = localStorage.getItem('auth_token');
    if (!token) {
      toast.error(c.loginRequired);
      navigate('/auth', { state: { returnTo: `/orbit/join/${inviteCode}` } });
      return;
    }

    try {
      setJoining(true);
      const response = await api.post(`/orbit/${orbitPreview.orbit_id}/join/${inviteCode}`);
      setJoined(true);
      toast.success(language === 'tr' ? 'Orbit\'e katıldınız!' : 'Joined Orbit successfully!');
    } catch (error) {
      console.error('Error joining orbit:', error);
      if (error.response?.status === 401) {
        toast.error(c.loginRequired);
        navigate('/auth', { state: { returnTo: `/orbit/join/${inviteCode}` } });
      } else {
        toast.error(language === 'tr' ? 'Katılınamadı' : 'Failed to join');
      }
    } finally {
      setJoining(false);
    }
  };

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ position: 'relative', zIndex: 1 }}>
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ position: 'relative', zIndex: 1 }}>
        <div className="text-center">
          <OrbitIcon className="w-16 h-16 text-[#E24A4A]/50 mx-auto mb-4" />
          <h1 className="font-serif text-2xl text-[#F8F5F2] mb-2">{c.invalidLink}</h1>
          <button
            onClick={() => navigate('/orbit')}
            className="mt-6 px-6 py-3 rounded-xl bg-[#D4AF37] text-[#030305] font-semibold"
          >
            {language === 'tr' ? 'Orbit\'e Dön' : 'Go to Orbit'}
          </button>
        </div>
      </div>
    );
  }

  if (joined) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", delay: 0.2 }}
          >
            <CheckCircle className="w-20 h-20 text-[#50C878] mx-auto mb-4" />
          </motion.div>
          <h1 className="font-serif text-2xl text-[#F8F5F2] mb-2">{c.joined}</h1>
          <p className="text-[#A1A1AA] mb-6">{orbitPreview.project_name}</p>
          <button
            onClick={() => navigate(`/orbit/${orbitPreview.orbit_id}`)}
            className="px-8 py-3 rounded-xl bg-[#D4AF37] text-[#030305] font-semibold flex items-center gap-2 mx-auto"
          >
            {c.goToOrbit}
            <ArrowRight className="w-5 h-5" />
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ position: 'relative', zIndex: 1 }}>
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="max-w-md w-full"
      >
        <div className="text-center mb-8">
          <OrbitIcon className="w-16 h-16 text-[#D4AF37] mx-auto mb-4" />
          <p className="text-[#A1A1AA] mb-2">{c.youreInvited}</p>
          <h1 className="font-serif text-3xl text-[#F8F5F2]">{orbitPreview.project_name}</h1>
        </div>

        <div className="glass-panel rounded-2xl p-6 mb-6 space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-[#A1A1AA] text-sm">{orbitPreview.project_type}</span>
            <div className="flex items-center gap-2 text-[#A1A1AA] text-sm">
              <Users className="w-4 h-4" />
              <span>{orbitPreview.member_count} {c.members}</span>
            </div>
          </div>
          
          <div className="pt-3 border-t border-white/10">
            <p className="text-xs text-[#A1A1AA] mb-1">{c.mission}</p>
            <p className="text-[#F8F5F2] text-sm">{orbitPreview.project_mission}</p>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#A1A1AA]">{c.status}:</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-[#D4AF37]/20 text-[#D4AF37]">
              {orbitPreview.current_status}
            </span>
          </div>
        </div>

        <button
          onClick={handleJoin}
          disabled={joining}
          className="w-full py-4 rounded-xl bg-[#D4AF37] text-[#030305] font-semibold text-lg hover:bg-[#D4AF37]/90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {joining ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {c.joining}
            </>
          ) : (
            <>
              <OrbitIcon className="w-5 h-5" />
              {c.joinNow}
            </>
          )}
        </button>

        <p className="text-center text-[#6B7280] text-xs mt-6">
          {language === 'tr' 
            ? 'Katılarak projenin bir parçası olacaksınız'
            : 'By joining, you\'ll become part of this project'
          }
        </p>
      </motion.div>
    </div>
  );
};

export default JoinOrbit;
